import { getDayjs } from './dayjs';

export type ConversionRightHistoryInput = { id?: number; startDate: string | Date }[];

type HistoryWithIdType<T extends ConversionRightHistoryInput> = T[number]['id'] extends number
	? (ConversionRightHistoryInput[number] & { id: number })[]
	: (ConversionRightHistoryInput[number] & { id: undefined })[];

type HistoryWithStartDateType<T extends ConversionRightHistoryInput> = T[number]['startDate'] extends string
	? (ConversionRightHistoryInput[number] & { startDate: string })[]
	: T[number]['startDate'] extends Date
		? (ConversionRightHistoryInput[number] & { startDate: Date })[]
		: ConversionRightHistoryInput;

type ConversionRightHistoryOutput<T extends ConversionRightHistoryInput> = (HistoryWithIdType<T>[number] &
	HistoryWithStartDateType<T>[number] & { endDate: string })[];

export type AddConversionRightHistoryEndDatesInput<T extends ConversionRightHistoryInput> = {
	conversionRightHistory: T;
	countAndRateHistory: { startDate: string | Date; endDate: string | Date; rightToConvert: boolean }[];
	childRateEndDate: Date | null;
};

export function addConversionRightHistoryEndDates<T extends ConversionRightHistoryInput>({
	conversionRightHistory,
	countAndRateHistory: countAndRateHistoryInput,
	childRateEndDate: childRateEndDateInput,
}: AddConversionRightHistoryEndDatesInput<T>): (T[number] & ConversionRightHistoryOutput<T>[number])[] {
	// Filter out history without the right to convert
	const countAndRateHistory = countAndRateHistoryInput.filter(({ rightToConvert }) => rightToConvert);

	// Sort by start date asc
	conversionRightHistory.sort((a, b) => (getDayjs(a.startDate).isBefore(getDayjs(b.startDate)) ? -1 : 1));

	countAndRateHistory.sort((a, b) => (getDayjs(a.startDate).isBefore(getDayjs(b.startDate)) ? -1 : 1));

	return conversionRightHistory.map((conversionRight, i) => {
		const conversionRightStart = getDayjs(conversionRight.startDate);
		const nextConversionRightStart = conversionRightHistory[i + 1]
			? getDayjs(conversionRightHistory[i + 1]?.startDate as string | Date)
			: null;

		const nearestCountAndRateEnd = countAndRateHistory.find((countAndRate, j) => {
			if (j + 1 === countAndRateHistory.length) return true;

			const countAndRateEnd = getDayjs(countAndRate.endDate);
			const nextCountAndRateStart = getDayjs(countAndRateHistory[j + 1]?.startDate as string | Date);

			return (
				countAndRateEnd.isSameOrAfter(conversionRightStart) &&
				!nextCountAndRateStart.isSame(countAndRateEnd.add(1, 'day'), 'day')
			);
		});
		const countAndRateEndDate = getDayjs(nearestCountAndRateEnd?.endDate as Date);

		const childRateEndDate = childRateEndDateInput ? getDayjs(childRateEndDateInput) : null;

		const conversionRightEndDate = nextConversionRightStart
			? nextConversionRightStart.subtract(1, 'day')
			: childRateEndDate?.isBefore(countAndRateEndDate)
				? childRateEndDate
				: countAndRateEndDate;

		return {
			...conversionRight,
			endDate: conversionRightEndDate.format('YYYY-MM-DD'),
		};
	}) as (T[number] & ConversionRightHistoryOutput<T>[number])[];
}

export function getSequencingSets<T>(sequencingTiers: ({ setNumber: number } & T)[]) {
	type SequencingTier = { setNumber: number } & T;

	return sequencingTiers
		.reduce<SequencingTier[][]>((acc, tier) => {
			acc[tier.setNumber] ??= [];
			acc[tier.setNumber]!.push(tier);
			return acc;
		}, [])
		.filter((set) => set?.length) // Filter out sets with no tiers
		.sort((a, b) => b[0]!.setNumber - a[0]!.setNumber); // Sort sets desc
}
